// imports section Start
import React, { useState, useEffect } from "react";
import { Box, Typography, IconButton } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import no_image from "../../assets/images/no_image.png";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import { SERVERIP } from "../../config/Config";
// imports section End

const GuestSlider = ({ promotionalMenu, IMAGEBASEURL }) => {
  // Hooks start
  const theme = useTheme();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [hovering, setHovering] = useState(false);
  // Hooks end

  // Handlers start
  // Process IMAGEPATH to create full URLs for the images
  const images = promotionalMenu.map((item) => {
    if (!item.IMAGEPATH) return no_image; 
    let imagePath = item.IMAGEPATH.replace("~", ""); 
    if (imagePath.includes(SERVERIP)) {
      return imagePath;
    }
    else{
      return `${IMAGEBASEURL}${imagePath}`;
    }
  });

  // Auto-slide interval
  useEffect(() => {
    if (!hovering && images.length > 0) {
      const interval = setInterval(() => {
        setCurrentImageIndex((prevIndex) =>
          prevIndex === images.length - 1 ? 0 : prevIndex + 1
        );
      }, 3000);

      return () => clearInterval(interval); // Cleanup
    }
  }, [images, hovering]);

  // Handlers for manual navigation
  const handleNext = () => {
    setCurrentImageIndex((prevIndex) =>
      prevIndex === images.length - 1 ? 0 : prevIndex + 1
    );
  };

  const handlePrev = () => {
    setCurrentImageIndex((prevIndex) =>
      prevIndex === 0 ? images.length - 1 : prevIndex - 1
    );
  };
  // Handlers end

  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        height: "60vh",
        overflow: "hidden",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
      onMouseEnter={() => setHovering(true)}
      onMouseLeave={() => setHovering(false)}
    >
      {/* Image Slider */}
      <Box
        component="img"
        src={images[currentImageIndex]}
        alt={`Promotional food ${currentImageIndex + 1}`}
        sx={{
          width: "100%",
          height: "100%",
          objectFit: "cover",
          transition: "transform 0.8s ease-in-out, opacity 0.8s ease-in-out",
          transform: "scale(1.1)", // Subtle zoom effect
        }}
      />

      {/* Overlay with Text */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          background: `linear-gradient(to bottom, rgba(0, 0, 0, ${
            theme.palette.mode === "dark" ? 0.7 : 0.4
          }), transparent)`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color:
            theme.palette.mode === "dark"
              ? theme.palette.common.white
              : theme.palette.common.white,
          zIndex: 2,
        }}
      >
        <Typography
          variant="h4"
          sx={{
            fontWeight: "bold",
            textAlign: "center",
            px: 2,
            textShadow: "0px 4px 8px rgba(0, 0, 0, 0.7)",
          }}
        >
          {promotionalMenu[currentImageIndex]?.MENUNAME ||
            "Delicious Food Awaits"}
        </Typography>
      </Box>

      {/* Navigation Buttons */}
      <IconButton
        onClick={handlePrev}
        sx={{
          position: "absolute",
          top: "50%",
          left: "16px",
          transform: "translateY(-50%)",
          backgroundColor: "rgba(0, 0, 0, 0.5)",
          color: theme.palette.common.white,
          zIndex: 3,
          "&:hover": {
            backgroundColor: "rgba(0, 0, 0, 0.7)",
          },
        }}
      >
        <ArrowBackIosIcon />
      </IconButton>
      <IconButton
        onClick={handleNext}
        sx={{
          position: "absolute",
          top: "50%",
          right: "16px",
          transform: "translateY(-50%)",
          backgroundColor: "rgba(0, 0, 0, 0.5)",
          color: theme.palette.common.white,
          zIndex: 3,
          "&:hover": {
            backgroundColor: "rgba(0, 0, 0, 0.7)",
          },
        }}
      >
        <ArrowForwardIosIcon />
      </IconButton>

      {/* Indicator Dots */}
      <Box
        sx={{
          position: "absolute",
          bottom: 16,
          display: "flex",
          justifyContent: "center",
          width: "100%",
          zIndex: 3,
        }}
      >
        {images.map((_, index) => (
          <Box
            key={index}
            onClick={() => setCurrentImageIndex(index)}
            sx={{
              width: 12,
              height: 12,
              borderRadius: "50%",
              backgroundColor:
                index === currentImageIndex
                  ? theme.palette.primary.main
                  : theme.palette.grey[500],
              margin: "0 4px",
              cursor: "pointer",
              transition: "background-color 0.3s",
            }}
          />
        ))}
      </Box>
    </Box>
  );
};

export default GuestSlider;
